import {Link} from 'react-router-dom';
import {useAuth} from '../context/AuthContext';
import {ROLE_LABELS,can,canAdministerCompany} from '../lib/permissions';
import React, { useEffect, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { validateAlertLead } from "../lib/alertSettings";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import SiteMonthSettings, {SiteHandoverDocumentSettings} from "../components/SiteMonthSettings";
import { Settings as SettingsIcon, BellRing, BookOpen, ShieldCheck, Mail, Loader2, UserRound, Activity } from "lucide-react";

export default function Settings() {
  const {user}=useAuth() || {};
    // Company settings are non-delegable (administrator only); site settings follow permissions.
  const isAdmin=canAdministerCompany(user),seesMonth=can(user,'tariffs.read'),seesDocuments=can(user,'rentals.read')&&can(user,'rentals.manage'),managesSites=seesMonth||seesDocuments;
  const [lead, setLead] = useState(null);
  const [everyone, setEveryone] = useState(false);
  const [activityBusy, setActivityBusy] = useState(false), [activityError, setActivityError] = useState("");
  const [version,setVersion]=useState('');
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let current = true; setLead(null); setLoadError("");
    api.get("/settings/company").then(({data}) => { if (current) {setLead(data.alert_lead);setEveryone(data.activity_visible_to_all===true);setVersion(data.updated_at||'');} })
      .catch(error => { if (current) setLoadError(apiError(error)); });
    return () => { current = false; };
  }, [attempt, isAdmin]);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailJob,setEmailJob]=useState(null),[emailError,setEmailError]=useState('');
  const emailPending=useRef(false);

  const sendDigest = async () => {
    if(emailPending.current)return;
    emailPending.current=true;setEmailError('');
    setEmailBusy(true);
    try {
      const { data } = await api.post("/notifications/send-digest");
      if(data.status==='queued'&&data.job_id){setEmailJob({job_id:data.job_id,state:'queued'});toast.success('Avvisi accodati. L’invio deve ancora completarsi.');}
      else if(data.simulated) toast.success(`Avvisi simulati: ${data.sent}/${data.recipients} destinatari. Nessuna email inviata.`);
      else toast.success(`Avvisi inviati: ${data.sent}/${data.recipients} destinatari · ${data.deadlines} scadenze`);
    } catch (e) {
      setEmailError(apiError(e));
    } finally { emailPending.current=false;setEmailBusy(false); }
  };
  const verifyEmailJob=async()=>{
    if(emailPending.current||!emailJob)return;
    emailPending.current=true;setEmailBusy(true);setEmailError('');
    try{const {data}=await api.get(`/notifications/jobs/${encodeURIComponent(emailJob.job_id)}`);setEmailJob(data);}
    catch(error){setEmailError(apiError(error));}
    finally{emailPending.current=false;setEmailBusy(false);}
  };

  // Saved at once: one choice, nothing to fill in first.
  const toggleEveryone = async () => {
    setActivityBusy(true); setActivityError("");
    try {
      const { data } = await api.put("/settings/company", { activity_visible_to_all: !everyone },{headers:{'If-Match':version}});
      setEveryone(data.activity_visible_to_all===true);
      setVersion(data.updated_at||'');
      toast.success(data.activity_visible_to_all ? "Ora tutti vedono le attività di tutti" : "Ognuno vede le attività del proprio ruolo");
    } catch (error) { setActivityError(apiError(error)); }
    finally { setActivityBusy(false); }
  };

  const save = async () => {
    setSaveError("");
    let valid;
    try { valid = validateAlertLead(lead); }
    catch (error) { setSaveError(error.message); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", { alert_lead: valid },{headers:{'If-Match':version}});
      setLead(data.alert_lead);
      setVersion(data.updated_at||'');
      toast.success("Impostazioni aziendali salvate");
    } catch (error) { setSaveError(apiError(error)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center gap-3">
        <div className="icon-tile h-11 w-11" aria-hidden="true"><SettingsIcon className="h-6 w-6" /></div>
        <div><h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Impostazioni</h1><p className="text-sm text-muted-foreground">{isAdmin?"Il tuo account, le sedi e l’azienda":managesSites?"Il tuo account e le sedi":"Il tuo account"}</p></div>
      </div>

      <Card className="border-border bg-card p-6">
        <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold"><UserRound className="h-5 w-5 text-primary" aria-hidden="true"/> Il tuo account</h3>
        <dl className="grid gap-3 text-sm sm:grid-cols-3">
          <div><dt className="text-muted-foreground">Nome</dt><dd className="break-words font-medium">{user?.name||"-"}</dd></div>
          <div><dt className="text-muted-foreground">Email</dt><dd className="break-words font-medium">{user?.email||"-"}</dd></div>
          <div><dt className="text-muted-foreground">Ruolo</dt><dd className="font-medium">{ROLE_LABELS[user?.role]||user?.role||"-"}</dd></div>
        </dl>
      </Card>

      {isAdmin&&<>

      <Card className="border-border bg-card p-6">
        <h3 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold"><BellRing className="h-5 w-5 text-primary" /> Anticipo avvisi scadenze</h3>
        <p className="mb-4 text-sm text-muted-foreground">Scegli tre anticipi crescenti, in giorni, per i conteggi delle scadenze nella Dashboard. Le impostazioni valgono su tutti i dispositivi dell’azienda. La programmazione delle email resta separata.</p>
        {loadError ? <div role="alert"><p>{loadError}</p><Button variant="outline" className="mt-3" onClick={() => setAttempt(n => n + 1)}>Riprova caricamento</Button></div> : !lead ? <p role="status">Caricamento impostazioni aziendali…</p> : <>
        <fieldset disabled={saving} className="grid grid-cols-3 gap-4 sm:max-w-md">
          {["days7", "days15", "days30"].map((k, index) => (
            <div key={k}><Label htmlFor={`lead-${k}`} className="mb-1.5 block text-sm">Soglia {index + 1} (giorni)</Label><Input id={`lead-${k}`} type="number" min="0" step="1" required value={lead[k]} onChange={(e) => setLead((l) => ({ ...l, [k]: e.target.value }))} data-testid={`lead-${k}`} /></div>
          ))}
        </fieldset>
        {saveError && <p className="mt-3 text-sm text-red-300" role="alert">{saveError}</p>}
        <Button disabled={saving} className="mt-4" onClick={save} data-testid="save-settings-btn">{saving ? "Salvataggio…" : "Salva impostazioni"}</Button>
        </>}
      </Card>

      {lead&&<Card className="border-border bg-card p-6" data-testid="activity-visibility">
        <h3 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold"><Activity className="h-5 w-5 text-primary" aria-hidden="true" /> Chi vede le attività</h3>
        <p className="mb-4 text-sm text-muted-foreground">Normalmente Autista e Operatore vedono solo le proprie attività, il Fleet Manager anche quelle di Operatori e Autisti, l’Amministratore tutte. Restano comunque escluse le sezioni e le sedi a cui una persona non ha accesso.</p>
        <label className="flex cursor-pointer items-start gap-3">
          <button type="button" role="switch" aria-checked={everyone} disabled={activityBusy} onClick={toggleEveryone} data-testid="activity-everyone"
            className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-60 ${everyone ? "bg-primary" : "bg-secondary"}`}>
            <span className={`inline-block h-5 w-5 rounded-full bg-background shadow transition-transform ${everyone ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
          <span className="text-sm"><span className="font-medium">Tutti vedono le attività di tutti</span><span className="block text-muted-foreground">{everyone ? "Attivo: ogni utente vede anche le attività degli altri, Amministratore compreso." : "Disattivato."}</span></span>
        </label>
        {activityError && <p className="mt-3 text-sm text-red-300" role="alert">{activityError}</p>}
      </Card>}

      </>}

      {seesMonth&&<SiteMonthSettings user={user}/>}
      {seesDocuments&&<SiteHandoverDocumentSettings user={user}/>}

      {isAdmin&&<Card className="border-border bg-card p-6">
        <h3 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold"><Mail className="h-5 w-5 text-primary" /> Avvisi email scadenze</h3>
        <p className="mb-4 text-sm text-muted-foreground">Accoda il riepilogo per i destinatari autorizzati. L’invio richiede un servizio email configurato; la programmazione automatica va attivata separatamente.</p>
        <Button onClick={sendDigest} disabled={emailBusy} data-testid="send-digest-btn">
          {emailBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}Invia avvisi ora
        </Button>
        {emailError&&<p role="alert" className="mt-3 text-sm text-red-300">{emailError}</p>}
        {emailJob&&<div className="mt-4 space-y-3 text-sm"><p role="status" aria-atomic="true">Avvisi accodati.{emailJob.delivery?` In attesa: ${emailJob.delivery.queued}; confermati dal servizio email: ${emailJob.delivery.succeeded}; annullati: ${emailJob.delivery.cancelled}; da verificare: ${emailJob.delivery.reconciliation}; non riusciti: ${emailJob.delivery.failed}.`:' L’invio deve ancora completarsi.'}{emailJob.delivery?.simulated>0?` Simulati: ${emailJob.delivery.simulated}; nessuna email inviata per questi elementi.`:''}{emailJob.provider_configured===false?' Il servizio email non è configurato.':''}</p><Button variant="outline" disabled={emailBusy} onClick={verifyEmailJob}>Verifica stato invio</Button></div>}
      </Card>}

      <Card className="border-border bg-card p-6">
        <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold"><BookOpen className="h-5 w-5 text-primary" /> Guida iniziale</h3>
        <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
          <li><span className="text-foreground">Dashboard</span>: panoramica di veicoli, scadenze, costi e avvisi urgenti.</li>
          <li><span className="text-foreground">Veicoli</span>: aggiungi, modifica, duplica veicoli. Clicca una riga per la scheda completa con timeline.</li>
          <li><span className="text-foreground">Assegnazioni</span>: assegna un veicolo evitando sovrapposizioni. Usa "Consegne e riconsegne" per la checklist.</li>
          <li><span className="text-foreground">Manutenzioni / Scadenze</span>: pianifica interventi e monitora documenti con avvisi colorati (rosso/arancione/verde).</li>
          <li><span className="text-foreground">Costi / Rifornimenti</span>: registra tutte le spese; la dashboard e i report si aggiornano automaticamente.</li>
          <li><span className="text-foreground">Report / Calendario</span>: analizza costi e utilizzo, visualizza gli eventi nel mese.</li>
        </ol>
      </Card>

      {isAdmin&&<Card className="border-border bg-card p-6">
        <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold"><ShieldCheck className="h-5 w-5 text-primary" /> Ruoli e permessi</h3>
        <div className="space-y-3 text-sm"><p>Gestisci le sedi autorizzate e i permessi di ciascun utente. I Fleet Manager partono con statistiche operative abilitate e riepiloghi economici disabilitati.</p><Button asChild variant="outline"><Link to="/utenti">Apri Utenti e permessi</Link></Button></div>
      </Card>}
    </div>
  );
}
